import React, { useState, useRef, useEffect } from "react";
import { ArrowRight } from "lucide-react";
import Player from "@vimeo/player";

const stats = [
  { k: "Daily network events", v: "3.2B+" },
  { k: "Resolution", v: "15 min · 250 m" },
  { k: "Countries served", v: "5" },
  { k: "Anonymisation", v: "Configurable" },
];

const HeroSection = () => {
  const [videoPlaying, setVideoPlaying] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (!iframeRef.current) return;
    const player = new Player(iframeRef.current);
    player.on("playing", () => setVideoPlaying(true));
    return () => player.off("playing");
  }, []);

  const scrollToContact = () => {
    const contactSection = document.getElementById("contact-section");
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <section id="hero" className="relative pt-16 bg-paper">
      <div className="max-w-7xl mx-auto px-6 pt-20 pb-16 md:pt-28 md:pb-24 grid lg:grid-cols-12 gap-12 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-6">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1 rounded-full border border-paper-300 bg-paper-100">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-500" />
            <span className="text-xs font-semibold tracking-wide text-ink-muted uppercase">
              Multi-operator mobility intelligence
            </span>
          </div>

          <h1 className="font-display text-5xl md:text-6xl font-bold tracking-tight text-ink leading-[1.05]">
            Movement,
            <span className="block text-brand-500">made measurable.</span>
          </h1>

          <p className="mt-6 text-lg md:text-xl text-ink-muted max-w-xl leading-relaxed">
            Telcofy turns billions of mobile-network signals into transparent,
            validated mobility intelligence — through a neutral, multi-operator
            pipeline serving cities, statistical offices, and commercial
            partners across the Nordics and Baltics.
          </p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <button
              onClick={scrollToContact}
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-white bg-brand-500 rounded-md hover:bg-brand-600 transition-colors"
            >
              Talk to the team
              <ArrowRight size={16} />
            </button>
            <a
              href="#use-cases"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-brand-600 bg-transparent border border-brand-500 rounded-md hover:bg-brand-50 transition-colors"
            >
              Explore real projects
            </a>
            <a
              href="https://docs.telcofy.ai"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-3 text-sm font-semibold text-ink-soft hover:bg-paper-200 rounded-md transition-colors"
            >
              Read the docs
            </a>
          </div>

          <dl className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-2xl">
            {stats.map(({ k, v }) => (
              <div key={k}>
                <dt className="text-xs font-medium text-ink-subtle uppercase tracking-wide">
                  {k}
                </dt>
                <dd className="mt-1 font-display text-xl md:text-2xl font-bold text-ink leading-tight">
                  {v}
                </dd>
              </div>
            ))}
          </dl>
        </div>

        {/* Right: video panel */}
        <div className="lg:col-span-6">
          <div className="relative aspect-[4/3] rounded-xl overflow-hidden border border-paper-300 shadow-brand-card bg-paper-200">
            <div
              className={`absolute inset-0 transition-opacity duration-1000 ${
                videoPlaying ? "opacity-100" : "opacity-0"
              }`}
            >
              <iframe
                ref={iframeRef}
                src="https://player.vimeo.com/video/1165362942?background=1&autoplay=1&loop=1&muted=1&dnt=1"
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"
                style={{
                  border: "none",
                  width: "max(177.78vh, 100%)",
                  height: "max(56.25vw, 100%)",
                }}
                allow="autoplay; fullscreen"
                title="Telcofy ambient background"
              />
            </div>
            {!videoPlaying && (
              <div className="absolute inset-0 grid place-items-center">
                <div
                  className="absolute inset-0 opacity-[0.06]"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 1px 1px, #1F2937 1px, transparent 0)",
                    backgroundSize: "20px 20px",
                  }}
                />
                <img
                  src="/brand/telcofy-mark.svg"
                  alt=""
                  className="relative w-20 h-20 opacity-30"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
